/**
 * Custom Product Media — thumbnail gallery + Customily polaroid integration.
 *
 * Thumbnails switch the main product image. The Customily canvas (polaroid)
 * is hidden on load and only revealed when the user uploads an image.
 * Removing the image hides the polaroid and restores the product image.
 */

const SELECTORS = {
  container: '.custom-product-media',
  thumbnail: '.custom-product-media__thumbnail',
  thumbnailSelected: 'custom-product-media__thumbnail--selected',
  mainImage: '.custom-product-media__main-image',
  mainImageActive: 'custom-product-media__main-image--active',
  canvas: '.cl-canvas-container',
  canvasUpload: 'customily--has-upload',
  app: '.customily-main-app',
  fileInput: '.customily-main-app input[type="file"]',
  imagePreview: '[class*="preview"] img, [class*="thumbnail"] img, .cl-image-preview img',
};

// --- Thumbnail gallery ---

function initThumbnailGallery(container) {
  const thumbnails = container.querySelectorAll(SELECTORS.thumbnail);
  const mainImages = container.querySelectorAll(SELECTORS.mainImage);

  if (!thumbnails.length || !mainImages.length) return;

  for (const thumb of thumbnails) {
    thumb.addEventListener('click', () => {
      // Don't switch images while the polaroid is active (it shifts the canvas)
      if (container.querySelector(`.${SELECTORS.canvasUpload}`)) return;

      const mediaId = thumb.getAttribute('data-media-id');

      for (const t of thumbnails) t.classList.remove(SELECTORS.thumbnailSelected);
      thumb.classList.add(SELECTORS.thumbnailSelected);

      for (const img of mainImages) img.classList.remove(SELECTORS.mainImageActive);
      const target = container.querySelector(`${SELECTORS.mainImage}[data-media-id="${mediaId}"]`);
      if (target) target.classList.add(SELECTORS.mainImageActive);
    });
  }
}

// --- Customily polaroid visibility ---

function hideCanvas(canvas) {
  canvas.style.setProperty('display', 'none', 'important');
  canvas.classList.remove(SELECTORS.canvasUpload);
}

function showCanvas(canvas) {
  canvas.style.setProperty('display', '', '');
  canvas.classList.add(SELECTORS.canvasUpload);

  // Fully hide product images so they don't take up space below the canvas
  document.querySelectorAll(SELECTORS.mainImage).forEach((img) => {
    img.style.display = 'none';
  });

  // On smaller screens the polaroid may be off-screen after reveal — scroll to it
  requestAnimationFrame(() => {
    const wrapper = canvas.closest(SELECTORS.container) || canvas.parentElement;
    if (wrapper) {
      wrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });
}

function restoreProductImage() {
  // Restore all product images to their default display, then let the
  // --active class control which one is visible.
  document.querySelectorAll(SELECTORS.mainImage).forEach((img) => {
    img.style.display = '';
  });
}

function clearCustomilyStorage() {
  try {
    for (const store of [localStorage, sessionStorage]) {
      for (const key of Object.keys(store)) {
        if (key.toLowerCase().includes('customily')) store.removeItem(key);
      }
    }
  } catch (_) { /* storage unavailable */ }
}

// --- File-input hook (detect upload) ---

function hookFileInput(input, canvas) {
  if (input._customilyHooked) return;
  input._customilyHooked = true;

  input.addEventListener('change', () => {
    if (input.files?.length) {
      showCanvas(canvas);
      watchForImageRemoval(canvas);
    }
  });
}

function hookAllFileInputs(canvas) {
  document.querySelectorAll(SELECTORS.fileInput).forEach((input) => hookFileInput(input, canvas));
}

// --- Watch for image removal ---

function watchForImageRemoval(canvas) {
  const app = document.querySelector(SELECTORS.app);
  if (!app) return;

  const check = () => {
    if (!canvas.classList.contains(SELECTORS.canvasUpload)) return;

    const fileInput = app.querySelector('input[type="file"]');
    const hasFile = fileInput?.files?.length > 0;
    const hasPreview = !!app.querySelector(SELECTORS.imagePreview);

    if (!hasFile && !hasPreview) {
      hideCanvas(canvas);
      restoreProductImage();
    }
  };

  // Clicks on remove/delete buttons trigger Customily DOM updates
  app.addEventListener('click', () => setTimeout(check, 300));

  // DOM mutations in the Customily app (e.g. preview removed)
  new MutationObserver(() => {
    if (canvas.classList.contains(SELECTORS.canvasUpload)) setTimeout(check, 300);
  }).observe(app, { childList: true, subtree: true });
}

// --- Canvas discovery ---

function relocateCanvas(canvas) {
  // Customily injects .cl-canvas-container as a direct child of <body>.
  // This causes a layout shift when the canvas becomes visible because it's
  // outside the product grid. Move it into the product media wrapper so it
  // renders in-place without jumping.
  const wrapper = document.querySelector('.custom-product-media__main-wrapper');
  if (wrapper && canvas.parentElement !== wrapper) {
    wrapper.appendChild(canvas);
  }
}

function onCanvasFound(canvas) {
  hideCanvas(canvas);
  relocateCanvas(canvas);
  hookAllFileInputs(canvas);

  // Customily injects file inputs asynchronously — keep hooking new ones
  new MutationObserver(() => hookAllFileInputs(canvas))
    .observe(document.body, { childList: true, subtree: true });
}

function waitForCanvas() {
  const existing = document.querySelector(SELECTORS.canvas);
  if (existing) {
    onCanvasFound(existing);
    return;
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;

        const found = node.matches?.(SELECTORS.canvas)
          ? node
          : node.querySelector?.(SELECTORS.canvas);

        if (found) {
          onCanvasFound(found);
          observer.disconnect();
          return;
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

// --- Initialisation ---

function init() {
  document.querySelectorAll(SELECTORS.container).forEach(initThumbnailGallery);
  clearCustomilyStorage();
  waitForCanvas();
}

document.addEventListener('DOMContentLoaded', init);
document.addEventListener('shopify:section:load', (event) => {
  const container = event.target.querySelector(SELECTORS.container);
  if (container) initThumbnailGallery(container);
});
